import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { LuTrash2 } from 'react-icons/lu'

import {
  loadWebhookCatalogue,
  loadWebhooks,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  testWebhook,
  rotateWebhookKey,
  loadWebhookDeliveries,
  replayWebhookDelivery,
} from '../../../features/webhookSubscriptions/webhookSubscriptions'
import Dropdown from '../../../components/Dropdown/Dropdown'
import Input from '../../../components/Input/Input'
import TextArea from '../../../components/TextArea/TextArea'
import Button from '../../../components/Button/Button'
import ConfirmModal from '../../../components/ConfirmModal/ConfirmModal'
import { showSnackBar } from '../../../components/Snackbar/Snackbar'

const EVENT_LABELS = {
  LEAD_BECOMES_CONTACT: 'Lead becomes a client',
  LSA_SIGNED: 'Agreement signed',
  PLAN_ACTIVE: 'Payment plan becomes active',
  CONTACT_CANCELLED: 'Client cancelled',
  REVOKED_POA_SIGNED: 'Revoked power of attorney signed',

  QC_CALL_COMPLETE: 'Quality control call completed',
  WELCOME_CALL_COMPLETE: 'Welcome call completed',
  CEASE_DESIST_COMPLETE: 'Cease & desist completed',
  POWER_OF_ATTORNEY_COMPLETE: 'Power of attorney completed',
  INITIAL_DISPUTE_LETTER_COMPLETE: 'Initial dispute letter completed',
  SECOND_DISPUTE_LETTER_COMPLETE: 'Second dispute letter completed',
  SUBSEQUENT_DISPUTE_LETTER_COMPLETE: 'Subsequent dispute letter completed',
  FOLLOW_UP_30_DAY_COMPLETE: '30-day follow-up completed',
  FOLLOW_UP_60_DAY_COMPLETE: '60-day follow-up completed',
  FOLLOW_UP_90_DAY_COMPLETE: '90-day follow-up completed',
  FOLLOW_UP_120_DAY_COMPLETE: '120-day follow-up completed',

  DOCUMENT_CANCELLATION_REQUEST_MARKED: 'Cancellation request marked',
  DOCUMENT_LEGAL_MARKED: 'Legal document marked',
  DOCUMENT_SETTLEMENT_MARKED: 'Settlement letter marked',
  DOCUMENT_COLLECTION_MARKED: 'Collection letter marked',
  DOCUMENT_DISPUTE_RESPONSE_MARKED: 'Dispute response marked',
  DOCUMENT_UPLOADED: 'Document uploaded',

  PAYMENT_CLEARED: 'Payment cleared',
  PAYMENT_FAILED: 'Payment failed',
  PAYMENT_NSF: 'Payment returned NSF',
}

const FIELD_LABELS = {
  customer_id: 'Client reference',
  first_name: 'First name',
  last_name: 'Last name',
  email: 'Email',
  phone_number: 'Phone',
  mobile_phone: 'Mobile phone',
  enrollment_date: 'Enrolment date',
  payment_amount: 'Payment amount',
  payment_frequency: 'Payment frequency',
  payment_start_date: 'First payment date',
  transaction_amount: 'Charge amount',
  transaction_date: 'Charge due date',
  transaction_reference: 'Charge reference',
  failure_reason: 'Failure reason',
  agent_name: 'Agent on the file',
  lead_source: 'Lead source',
  client_status: 'Client status',
  payment_method: 'Payment method (card / ACH)',
  payment_number: 'Payment number',
  transaction_processed_date: 'Charge processed date',
  transaction_settled_date: 'Charge settled date',
  transaction_returned_date: 'Charge returned date',
  client_code: 'Client code (as shown in the CRM)',
}

const ENCRYPTION_MODES = [
  { value: 'none', label: 'Signed only (TLS)' },
  { value: 'rsa-oaep', label: 'Signed and encrypted (partner public key)' },
]

const EMPTY = {
  name: '', url: '', events: [], fields: [],
  enabled: false, encryptionMode: 'none', publicKey: '',
  headerName: '', headerValue: '', headerSaved: false,
}

const toMenuItems = (keys, labels) => (keys || []).map(key => ({ value: key, label: labels[key] || key }))

const OutboundWebhooks = ({ groupId }) => {
  const dispatch = useDispatch()

  const [catalogue, setCatalogue] = useState({ events: [], fields: [] })
  const [webhooks, setWebhooks] = useState([])
  const [copiedId, setCopiedId] = useState(null)
  const [rotatingId, setRotatingId] = useState(null)
  const [pendingRotate, setPendingRotate] = useState(null)
  const [draft, setDraft] = useState(EMPTY)
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [testingId, setTestingId] = useState(null)
  const [pendingDelete, setPendingDelete] = useState(null)
  const [logFor, setLogFor] = useState(null)
  const [deliveries, setDeliveries] = useState([])

  const refresh = useCallback(async () => {
    const result = await dispatch(loadWebhooks({ groupId }))
    if (!loadWebhooks.fulfilled.match(result)) return

    setWebhooks(result.payload?.webhooks || [])
  }, [dispatch, groupId])

  useEffect(() => {
    const load = async () => {
      const catalogueResult = await dispatch(loadWebhookCatalogue())
      if (loadWebhookCatalogue.fulfilled.match(catalogueResult)) setCatalogue(catalogueResult.payload)
      await refresh()
    }

    load()
  }, [dispatch, refresh])

  const startEditing = (webhook) => {
    setEditingId(webhook._id)
    setDraft({
      name: webhook.name || '',
      url: webhook.url || '',
      events: webhook.events || [],
      fields: webhook.fields || [],
      enabled: Boolean(webhook.enabled),
      encryptionMode: webhook.encryption?.mode || 'none',
      publicKey: '',
      headerName: webhook.auth?.headerName || '',
      headerValue: '',
      headerSaved: Boolean(webhook.auth?.valueSet),
    })
  }

  const stopEditing = () => {
    setEditingId(null)
    setDraft(EMPTY)
  }

  const save = async () => {
    setSaving(true)

    const { headerName, headerValue, headerSaved, ...rest } = draft

    const payload = {
      ...rest,
      encryption: draft.encryptionMode === 'rsa-oaep'
        ? { mode: 'rsa-oaep', publicKey: draft.publicKey }
        : { mode: 'none' },
      auth: { headerName: headerName.trim(), headerValue },
    }

    const action = editingId
      ? updateWebhook({ groupId, id: editingId, payload })
      : createWebhook({ groupId, payload })

    const result = await dispatch(action)
    setSaving(false)

    if (updateWebhook.fulfilled.match(result) || createWebhook.fulfilled.match(result)) {
      showSnackBar('success', editingId ? 'Webhook saved' : 'Webhook created')

      const warnings = result.payload?.warnings || []
      warnings.forEach(warning => showSnackBar('error', warning))

      stopEditing()
      await refresh()
      return
    }

    showSnackBar('error', result.payload || 'Could not save the webhook')
  }

  const remove = async () => {
    const id = pendingDelete
    const result = await dispatch(deleteWebhook({ groupId, id }))
    setPendingDelete(null)

    if (deleteWebhook.fulfilled.match(result)) {
      showSnackBar('success', 'Webhook removed')
      if (editingId === id) stopEditing()
      await refresh()
      return
    }

    showSnackBar('error', result.payload || 'Could not remove the webhook')
  }

  const copyKey = async (webhook) => {
    try {
      await navigator.clipboard.writeText(webhook.secret || '')
      setCopiedId(webhook._id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch (error) {
      showSnackBar('error', 'Could not copy the key')
    }
  }

  const rotate = async () => {
    const id = pendingRotate
    setPendingRotate(null)
    setRotatingId(id)

    const result = await dispatch(rotateWebhookKey({ groupId, id }))
    setRotatingId(null)

    if (rotateWebhookKey.fulfilled.match(result)) {
      showSnackBar('success', 'New key issued — give it to the partner')
      await refresh()
      return
    }

    showSnackBar('error', result.payload || 'Could not rotate the key')
  }

  const runTest = async (id) => {
    setTestingId(id)
    const result = await dispatch(testWebhook({ groupId, id }))
    setTestingId(null)

    if (!testWebhook.fulfilled.match(result)) {
      showSnackBar('error', result.payload || 'The test send could not be made')
      return
    }

    const { outcome, reason } = result.payload

    if (outcome === 'delivered') {
      showSnackBar('success', 'Test event delivered')
      return
    }

    showSnackBar('error', reason ? `Test event ${outcome}: ${reason}` : `Test event ${outcome}`)
  }

  const fetchLog = async (id) => {
    const result = await dispatch(loadWebhookDeliveries({ groupId, id }))

    if (loadWebhookDeliveries.fulfilled.match(result)) {
      setDeliveries(result.payload || [])
      return
    }

    showSnackBar('error', result.payload || 'Failed to load the delivery log')
  }

  const toggleLog = async (id) => {
    if (logFor === id) {
      setLogFor(null)
      return
    }

    setLogFor(id)
    setDeliveries([])
    await fetchLog(id)
  }

  const replay = async (id, deliveryId) => {
    const result = await dispatch(replayWebhookDelivery({ groupId, id, deliveryId }))

    if (!replayWebhookDelivery.fulfilled.match(result)) {
      showSnackBar('error', result.payload || 'The event could not be sent again')
      return
    }

    showSnackBar(result.payload.outcome === 'delivered' ? 'success' : 'error', `Replay: ${result.payload.outcome}`)
    await fetchLog(id)
  }

  const canSave = Boolean(
    draft.name.trim()
    && draft.url.trim()
    && draft.events.length
    && draft.fields.length
    && (editingId || draft.encryptionMode !== 'rsa-oaep' || draft.publicKey.trim())
    && (!draft.headerName.trim() || draft.headerValue.trim() || draft.headerSaved)
  )

  return (
    <div className="w-full flex justify-center mt-8">
      <div className="border border-divider bg-surface shadow-lg rounded-2xl w-[775px] px-8 py-6">
        <h3 className="text-lg font-semibold mb-1">Outbound — events we send to a partner</h3>
        <p className="text-sm text-text-secondary mb-2">
          A feed configured here reaches this office&apos;s clients and nobody else. We issue the signing
          key — give it to the partner so they can verify that a delivery came from us.
        </p>

        <div className="flex flex-col gap-3">
          {webhooks.length === 0 && (
            <p className="text-sm text-text-secondary">Nothing is sent to a partner yet.</p>
          )}

          {webhooks.map(webhook => (
            <div key={webhook._id} className="border border-divider rounded-lg p-4 flex flex-col gap-2">
              <div className="flex items-center justify-between gap-4">
                <div className="flex flex-col">
                  <span className="font-medium text-text-primary">{webhook.name}</span>
                  <span className="text-xs text-text-secondary break-all">{webhook.url}</span>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span className="text-xs text-text-secondary">
                    {webhook.enabled ? 'Sending' : 'Not sending'}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 text-xs">
                <span className="text-text-secondary shrink-0">Signing key</span>
                <code className="font-mono text-text-primary break-all bg-surface px-2 py-1 rounded">
                  {webhook.secret}
                </code>
                <Button type="outlined" size="sm" onClick={() => copyKey(webhook)}>
                  {copiedId === webhook._id ? 'Copied' : 'Copy'}
                </Button>
                <Button
                  type="outlined"
                  size="sm"
                  disabled={rotatingId === webhook._id}
                  onClick={() => setPendingRotate(webhook._id)}
                >
                  {rotatingId === webhook._id ? 'Rotating…' : 'Rotate'}
                </Button>
              </div>

              <div className="text-xs text-text-secondary">
                {webhook.encryption?.mode === 'rsa-oaep'
                  ? `Encrypted (${webhook.encryption.fingerprint})`
                  : 'Signed'}
                {' · '}
                {(webhook.events || []).map(event => EVENT_LABELS[event] || event).join(' · ')}
              </div>

              {webhook.auth?.headerName && (
                <div className="text-xs text-text-secondary">
                  {`Sends header ${webhook.auth.headerName} with the partner's key`}
                </div>
              )}

              {webhook.previousKeyAcceptedUntil && (
                <div className="text-xs text-text-secondary">
                  The previous key is still accepted until
                  {' '}
                  {new Date(webhook.previousKeyAcceptedUntil).toLocaleString()}
                  {' — after that only the key above verifies.'}
                </div>
              )}

              <div className="flex gap-2 mt-2">
                <Button type="outlined" size="sm" onClick={() => startEditing(webhook)}>Edit</Button>
                <Button
                  type="outlined"
                  size="sm"
                  disabled={testingId === webhook._id}
                  onClick={() => runTest(webhook._id)}
                >
                  {testingId === webhook._id ? 'Sending…' : 'Test'}
                </Button>
                <Button type="outlined" size="sm" onClick={() => toggleLog(webhook._id)}>
                  {logFor === webhook._id ? 'Hide log' : 'Delivery log'}
                </Button>
                <Button type="outlined" size="sm" onClick={() => setPendingDelete(webhook._id)}>
                  Remove
                </Button>
              </div>

              {logFor === webhook._id && (
                <div className="mt-3 border-t border-divider pt-3 flex flex-col gap-1">
                  {deliveries.length === 0 && (
                    <span className="text-xs text-text-secondary">
                      No real events yet. Test sends are deliberately not recorded here.
                    </span>
                  )}

                  {deliveries.map(row => (
                    <div key={row._id} className="flex items-center justify-between gap-3 text-xs">
                      <span className="text-text-primary">
                        {EVENT_LABELS[row.eventKey] || row.eventKey}
                      </span>
                      <span className="text-text-secondary">
                        {row.status}
                        {row.attempts ? ` · ${row.attempts} of ${row.maxAttempts}` : ''}
                        {row.lastReason ? ` · ${row.lastReason}` : ''}
                      </span>
                      {row.status !== 'delivered' && (
                        <Button type="outlined" size="sm" onClick={() => replay(webhook._id, row._id)}>
                          Send again
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}

            </div>
          ))}
        </div>

        <div className="border-t border-divider mt-6 pt-6 flex flex-col gap-4">
          <span className="font-medium text-text-primary">
            {editingId ? 'Edit webhook' : 'Add a webhook'}
          </span>

          <Input
            label="Name *"
            placeholder="The partner's name"
            value={draft.name}
            setInput={(value) => setDraft({ ...draft, name: value })}
          />

          <Input
            label="Destination *"
            placeholder="https://partner.example/hooks"
            value={draft.url}
            setInput={(value) => setDraft({ ...draft, url: value })}
          />

          <Dropdown
            label="Protection *"
            placeholder="How the payload is protected"
            value={draft.encryptionMode}
            setValue={(encryptionMode) => setDraft({ ...draft, encryptionMode })}
            menuItems={ENCRYPTION_MODES}
            className="w-full"
          />

          {draft.encryptionMode === 'rsa-oaep' && (
            <TextArea
              title={editingId ? 'Replace public key (leave empty to keep the current one)' : 'Partner public key (PEM) *'}
              placeholder="-----BEGIN PUBLIC KEY-----"
              text={draft.publicKey}
              setText={(value) => setDraft({ ...draft, publicKey: value })}
            />
          )}

          <div className="flex flex-col gap-2">
            <span className="text-sm text-text-primary">Authentication header (optional)</span>
            <span className="text-xs text-text-secondary">
              Only if the partner requires its own key on every request. The key is never shown again after saving.
            </span>

            <Input
              label="Header name"
              placeholder="x-api-key"
              value={draft.headerName}
              setInput={(value) => setDraft({ ...draft, headerName: value })}
            />

            <Input
              label={draft.headerSaved ? 'Header value (saved — leave empty to keep it)' : 'Header value'}
              placeholder="The partner's key"
              type="password"
              autoComplete="new-password"
              value={draft.headerValue}
              setInput={(value) => setDraft({ ...draft, headerValue: value })}
            />

            {draft.headerSaved && (
              <div>
                <Button
                  type="outlined"
                  size="sm"
                  onClick={() => setDraft({ ...draft, headerName: '', headerValue: '', headerSaved: false })}
                >
                  Remove header
                </Button>
              </div>
            )}
          </div>

          <Dropdown
            label="Events *"
            placeholder="Which events this partner receives"
            value={draft.events}
            setValue={(events) => setDraft({ ...draft, events })}
            menuItems={toMenuItems(catalogue.events, EVENT_LABELS)}
            isMulti={true}
            className="w-full"
          />

          <Dropdown
            label="Fields to send *"
            placeholder="What each event carries"
            value={draft.fields}
            setValue={(fields) => setDraft({ ...draft, fields })}
            menuItems={toMenuItems(catalogue.fields, FIELD_LABELS)}
            isMulti={true}
            className="w-full"
          />

          <label className="flex items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })}
            />
            Send events to this destination
          </label>

          <div className="flex gap-2">
            <Button disabled={!canSave || saving} onClick={save}>
              {saving ? 'Saving…' : (editingId ? 'Save' : 'Create')}
            </Button>
            {editingId && <Button type="outlined" onClick={stopEditing}>Cancel</Button>}
          </div>
        </div>
      </div>

      {pendingRotate && (
        <ConfirmModal
          leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />}
          title="Issue a new signing key?"
          modalButtonTitle="Rotate"
          open={pendingRotate}
          setOpen={setPendingRotate}
          functionButton={rotate}
        >
          Deliveries will be signed with the new key immediately, and the old key is still accepted for
          24 hours so the partner has time to install the new one. Give it to them today.
        </ConfirmModal>
      )}

      {pendingDelete && (
        <ConfirmModal
          leftTopIcon={<LuTrash2 size={18} className="text-text-secondary" />}
          title="Remove this webhook?"
          modalButtonTitle="Remove"
          open={pendingDelete}
          setOpen={setPendingDelete}
          functionButton={remove}
        >
          The partner will stop receiving these events.
        </ConfirmModal>
      )}
    </div>
  )
}

export default OutboundWebhooks
